/**
 * AnimatedAvatar – Immozwerg Maskottchen
 * ─────────────────────────────────────
 * Neues Verhalten (nutzerfreundlich):
 *  1. Beim ersten Seitenaufruf: nach 5s kurz von oben reinbaumeln (hängend)
 *  2. Nutzer kann ihn per X-Button oder Klick wegschicken → merkt sich das in sessionStorage
 *  3. Danach: nur noch bei 60s Inaktivität (keine Mausbewegung, kein Scroll, kein Klick) kurz erscheinen
 *  4. Kein automatisches Dauerplopppen
 *  5. Kein WhatsApp-Popup beim Klick – nur wegklicken
 *
 * WICHTIG: Optik des Immozwergs niemals verändern
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X } from "lucide-react";

const HAENGEND_POSE = "/assets/c29a48c6ae8f.webp";

const MESSAGES = [
  "Hi, mein Name ist Immozwerg – schön dich kennenzulernen! 💜",
  "Noch Fragen? Schreib uns auf WhatsApp! 📱",
  "Kostenlose Bewertung gefällig? 🏡",
  "Andrea ist für dich da – einfach anschreiben! 👋",
];

const INACTIVITY_MS = 60_000; // 60 Sekunden Inaktivität
const FIRST_SHOW_MS = 5_000;  // 5s nach Seitenaufruf
const VISIBLE_MS    = 8_000;  // 8s sichtbar, dann wieder weg

const SESSION_KEY = "immozwerg_dismissed";

interface AnimatedAvatarProps {
  onWhatsAppClick: () => void;
}

export default function AnimatedAvatar({ onWhatsAppClick }: AnimatedAvatarProps) {
  const [visible, setVisible] = useState(false);
  const [slideIn, setSlideIn] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);
  // Der erste Render muss auf Server und Client identisch sein. Die bestehende
  // Session-Präferenz wird erst nach der Hydration aus sessionStorage gelesen.
  const [dismissed, setDismissed] = useState(false);

  const hideTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHovered   = useRef(false);
  const shownOnce   = useRef(false);

  const clearTimers = () => {
    if (hideTimer.current)  clearTimeout(hideTimer.current);
    if (idleTimer.current)  clearTimeout(idleTimer.current);
  };

  const dismiss = useCallback(() => {
    clearTimers();
    setSlideIn(false);
    setTimeout(() => setVisible(false), 600);
    if (typeof window !== "undefined") sessionStorage.setItem(SESSION_KEY, "1");
    setDismissed(true);
  }, []);

  const hide = useCallback(() => {
    if (isHovered.current) return;
    setSlideIn(false);
    setTimeout(() => setVisible(false), 600);
  }, []);

  const show = useCallback(() => {
    if (dismissed) return;
    const idx = Math.floor(Math.random() * MESSAGES.length);
    setMsgIndex(idx);
    setVisible(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setSlideIn(true));
    });
    hideTimer.current = setTimeout(() => {
      if (!isHovered.current) hide();
    }, VISIBLE_MS);
  }, [dismissed, hide]);

  const resetIdleTimer = useCallback(() => {
    if (dismissed) return;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      if (!visible) show();
    }, INACTIVITY_MS);
  }, [dismissed, visible, show]);

  // Persistierte Session-Präferenz erst nach dem identischen SSR-/Client-Erstrender lesen.
  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1") {
      setDismissed(true);
    }
  }, []);

  // Erster Aufruf: nach 5s zeigen (nur wenn nicht schon dismissed)
  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => {
      if (!shownOnce.current) {
        shownOnce.current = true;
        show();
      }
    }, FIRST_SHOW_MS);
    return () => clearTimeout(t);
  }, [dismissed, show]);

  // Idle-Tracking: Maus, Scroll, Klick
  useEffect(() => {
    if (dismissed) return;
    const events = ["mousemove", "scroll", "keydown", "touchstart", "click"];
    const handler = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimers();
    };
  }, [dismissed, resetIdleTimer]);

  if (!visible) return null;

  return (
    <>
      <div
        className="immozwerg-avatar-teaser pointer-events-none fixed right-6 top-24 z-[70] hidden select-none flex-col items-center sm:flex"
        style={{
          transform: slideIn ? "translateY(0)" : "translateY(-220px)",
          transition: "transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onMouseEnter={() => {
          isHovered.current = true;
          if (hideTimer.current) clearTimeout(hideTimer.current);
        }}
        onMouseLeave={() => {
          isHovered.current = false;
          hideTimer.current = setTimeout(() => hide(), 3000);
        }}
      >
        <div className="relative pointer-events-auto">
          <button
            type="button"
            onClick={onWhatsAppClick}
            className="block rounded-full focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-[#facc15]"
            aria-label="Kontakt über WhatsApp öffnen"
          >
            <img
              src={HAENGEND_POSE}
              alt=""
              width={220}
              height={442}
              loading="lazy"
              decoding="async"
              style={{
                width: 110,
                height: "auto",
                filter: "drop-shadow(0 4px 16px rgba(99,17,114,0.3))",
                animation: slideIn ? "avatarSway 3s ease-in-out infinite" : "none",
              }}
            />
          </button>

          <button
            type="button"
            onClick={dismiss}
            className="absolute -top-1 -right-1 flex size-6 items-center justify-center rounded-full border border-[#e8dff0] bg-white shadow-md transition-colors hover:bg-red-50 focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#631172]"
            aria-label="Immozwerg-Hinweis schließen"
          >
            <X size={12} className="text-gray-500" aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          onClick={onWhatsAppClick}
          className="pointer-events-auto mt-1 max-w-[140px] rounded-2xl rounded-t-sm border border-[#e8dff0] bg-white px-3 py-2 text-center shadow-lg transition-colors hover:bg-[#f9f5ff] focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-[#631172]"
          style={{ animation: "bubblePop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards" }}
        >
          <span className="block text-xs font-semibold leading-snug text-[#2d1b4e]">{MESSAGES[msgIndex]}</span>
          <span className="mt-1 block text-[10px] font-bold text-[#128c3b]">WhatsApp öffnen →</span>
        </button>
      </div>

      <button
        type="button"
        onClick={onWhatsAppClick}
        className="immozwerg-mobile-whatsapp-trigger fixed bottom-4 right-4 z-[70] inline-flex min-h-11 items-center gap-2 rounded-full bg-[#075e54] px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-[transform,background-color] hover:scale-105 hover:bg-[#054c44] focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-[#facc15] active:scale-[0.98] sm:hidden"
        aria-label="Kontakt über WhatsApp öffnen"
      >
        <MessageCircle size={19} aria-hidden="true" />
        WhatsApp
      </button>

      <style>{`
        @keyframes avatarSway {
          0%, 100% { transform: rotate(-3deg); }
          50%       { transform: rotate(3deg); }
        }
        @keyframes bubblePop {
          0%   { opacity: 0; transform: scale(0.7) translateY(-6px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}
